import * as L from 'leaflet';
import { PendingPlaybackRef } from '../contracts/playback-controller';
import { PlaybackStep } from '../contracts/vessel.types';
import { AisMapContext } from '../contracts/ais-map.context';

/**
 * The provider-specific answers this module must be TOLD rather than know
 * (rule R5 — no provider imports in `ais/shared/`):
 */
export interface SnapshotOptions {
  /**
   * "12 Jul 2026" — the date style the pending playback row's window label
   * uses. Injected because the formatter lives with the provider's
   * refresh-history panel model, which shared code must not import.
   */
  formatDate: (timeMs: number) => string;
  /**
   * The live map context changed — what is drawn, and therefore what is
   * searchable. Currently the facade's `pushSearchContext`; becomes the search
   * feature's `onLiveLayer` hook when features register (phase 5).
   */
  pushSearchContext: () => void;
  /**
   * Which provider's payload shape `host.convertToGeoJSONNew` should read a
   * playback frame as — Voyage Radar passes `'VOYAGERADAR'`.
   *
   * Injected in phase 7, where the rule-R5 grep found this module passing that
   * literal itself. It is the only vendor name that had survived in shared
   * CODE, and it survived the D8 sweep because that grep was case-sensitive
   * against `VoyageRadar` — a good argument for greps that match the concept
   * rather than the spelling.
   */
  geoJsonType: string;

  // ── The saved-capture archive (decision D8) ─────────────────────────────────
  // Snapshot playback over a server-side archive is a PROVIDER capability, not
  // a platform one — AISstream replays from its own SSE buffer and has no
  // archive at all. So this module never learns what a capture record looks
  // like: it is told how to list them, date them, name them and load them, the
  // same way `TargetController` is told `ownScene`. The record stays `any`
  // because nothing here inspects it. See `vr-snapshot-archive.gateway.ts`.
  /** Every archived capture on the open sensor, in storage order. */
  archive: () => any[];
  /** When a capture was taken (ms), or null when it cannot be replayed. */
  captureTime: (record: any) => number | null;
  /** The label a capture carries back to the panel. */
  captureLabel: (record: any) => string;
  /** The stored id of a capture — what marks the row being played. */
  captureId: (record: any) => string | null;
  /** A fingerprint that changes whenever the archive does (the cache key). */
  archiveKey: () => string;
  /** The vessel features stored for one capture, or null if unreadable. */
  loadCapture: (record: any) => Promise<any[] | null>;
}

/**
 * AOI snapshots — the live vessel layer, frame playback over saved refreshes,
 * and the toolbar's Refresh button. Extracted verbatim from
 * `VoyageMapController` (phase 4, REFACTOR-PLAN.md §7); peers are reached
 * through `ctx` ports only (rule R2 — never a sibling import).
 *
 * Owns the layer/feature bookkeeping the rest of the map is downstream of:
 * which layer holds the LIVE vessels, which features are currently visible,
 * and the per-session cache of loaded playback frames. The display pass,
 * the selection and the located overlay all read what this module settles.
 */
export class SnapshotController {
  /** Single Leaflet layer holding the currently rendered playback frame. */
  playbackLayer: any = null;
  /** True while AIS vessels are on the map — gates the live toolbar. */
  dataActive = false;
  /**
   * The layer currently holding the LIVE vessels. Written by both host entry
   * paths (sensor click → aisVesselLayer, refresh-history display →
   * markersLayer) so the toolbar, legend and filter work regardless of source.
   */
  liveLayer: any = null;
  /** The features currently visible (live snapshot or playback frame). */
  currentFeatures: any[] = [];
  /**
   * The live snapshot's features, kept separately from `currentFeatures` —
   * which playback overwrites frame by frame. Exiting playback needs the live
   * set back to restore the type categories, and the layer itself does not
   * carry them in a form worth re-deriving.
   */
  private _liveFeatures: any[] = [];
  /** Every snapshot loaded this playback session — also feeds free path tracing. */
  loadedSteps: PlaybackStep[] = [];
  /** Window + history fingerprint `loadedSteps` was built from; '' means unusable. */
  private _stepsCacheKey = '';

  /** True while playback has temporarily hidden the live vessel layer. */
  private _liveLayerHidden = false;
  /** Resolver for the in-flight toolbar refresh (null when none is pending). */
  private _refreshResolve: (() => void) | null = null;
  /** True once a toolbar refresh actually issued a request (vs hitting a guard). */
  private _refreshStarted = false;

  /**
   * MMSI → the vessel's live name/colour, for every vessel in the snapshot on
   * the map. The Vessel Tracks panel reads it to decide the presence dot and to
   * prefer a name being broadcast NOW over one stored with a track last week.
   *
   * Built once per pull rather than derived per change-detection pass — a busy
   * AOI is hundreds of features and the panel would otherwise walk them all
   * several times a second.
   */
  liveVessels = new Map<string, { name?: string | null; color?: string | null }>();

  /** Encoded vessel glyphs, keyed by view + colour. See `vesselIconUrl`. */
  private _iconCache = new Map<string, string>();

  constructor(
    private readonly ctx: AisMapContext,
    private readonly opts: SnapshotOptions,
  ) {}

  /**
   * The live snapshot's feature list, read-only — what the free search index
   * and the snapshot-properties lookup walk. Distinct from `currentFeatures`,
   * which playback overwrites frame by frame.
   */
  get liveFeatures(): any[] {
    return this._liveFeatures;
  }

  // ── Snapshot loading ────────────────────────────────────────────────────────
  /**
   * The refresh-history records a replay of [from, to] would fetch.
   *
   * One place rather than two: `describePlayback` counts these on the click and
   * `loadPlaybackSteps` walks them a moment later, and a pending row promising
   * twelve frames over a load that only ever tries eight would be worse than no
   * pending row at all.
   */
  private playbackRecordsIn(fromIso: string, toIso: string): any[] {
    const history: any[] = this.opts.archive();
    const fromMs = new Date(fromIso).getTime();
    const toMs = new Date(toIso).getTime();
    return history.filter((h) => {
      const t = this.opts.captureTime(h);
      return t !== null && t >= fromMs && t <= toMs;
    });
  }

  /**
   * Two or more replayable snapshots exist — the toolbar's Playback control asks
   * before drawing itself, and the sidebar's pill applies the same rule through
   * `VoyageRefreshHistoryComponent.canPlayback`. Below two frames the transport
   * is dead on arrival (`PlaybackController.play()` refuses), and a POI task has
   * no scene to flip through however long its archive grows.
   *
   * Read on every change-detection pass, so it counts rather than collects and
   * stops at the second hit — no array is built and a 300-record archive costs
   * two iterations.
   */
  get canPlayHistory(): boolean {
    const history: any[] = this.opts.archive();
    let playable = 0;
    for (const h of history) {
      if (this.opts.captureTime(h) === null) continue;
      if (++playable >= 2) return true;
    }
    return false;
  }

  /**
   * What the pending ON MAP row says while a replay's frames are in the air.
   *
   * The snapshot twin of `pendingRefFor`. Both answer synchronously off data the
   * panel already holds, so the wait describes the thing that was clicked from
   * the first frame — "18 snapshots · 12 Jul 2026 – 28 Jul 2026" rather than an
   * anonymous spinner, and rather than the REFRESHING line this load used to
   * borrow, which claimed a paid pull was in flight when nothing was being
   * bought at all.
   *
   * Null when the window holds nothing: there is no honest wait to describe, and
   * the empty-archive dialog is a better answer than a row that counts to zero.
   */
  describePlayback(fromIso: string, toIso: string): PendingPlaybackRef | null {
    const records = this.playbackRecordsIn(fromIso, toIso);
    if (!records.length) return null;

    const times = records
      .map((h) => this.opts.captureTime(h))
      .filter((t): t is number => t !== null);
    const window = times.length
      ? `${this.opts.formatDate(Math.min(...times))} – ${this.opts.formatDate(Math.max(...times))}`
      : null;
    return { frames: records.length, window };
  }

  /**
   * Loads the refresh-history snapshots that fall inside the given TOI window
   * and turns each into a playback frame. Each snapshot is an already paid-for
   * full-AOI capture stored on S3, so replaying them costs 0 credits.
   */
  async loadPlaybackSteps(fromIso: string, toIso: string): Promise<PlaybackStep[]> {
    const sensor = this.ctx.sensor;
    const history: any[] = this.opts.archive();
    if (!sensor || !history.length) return [];

    const inWindow = this.playbackRecordsIn(fromIso, toIso);

    // Re-entering playback over the same window used to re-fetch every frame —
    // a round trip per capture, for data already parsed and still held in
    // `loadedSteps`. The provider's `archiveKey` carries whatever makes its
    // archive unique (for VR: sensor, length and newest id), so a refresh taken
    // in between still invalidates this.
    const cacheKey = [fromIso, toIso, this.opts.archiveKey()].join('|');
    if (cacheKey === this._stepsCacheKey && this.loadedSteps.length) {
      // Types are recomputed rather than assumed: exiting playback resets the
      // filter, so the categories have to be seeded again on every entry.
      const cachedUnion: any[] = [];
      for (const step of this.loadedSteps) cachedUnion.push(...(step.features ?? []));
      this.ctx.display.computeAvailableTypes(cachedUnion);
      return this.loadedSteps;
    }

    const loaded = await Promise.all(
      inWindow.map(async (h) => {
        const features = await this.opts.loadCapture(h);
        if (features == null) return null;
        return {
          time: this.opts.captureLabel(h),
          // Carried so the frame can name its own row back to the panel.
          refreshId: this.opts.captureId(h),
          features,
        } as PlaybackStep;
      })
    );
    const steps = loaded.filter(
      (s): s is PlaybackStep => !!s && Array.isArray(s.features)
    );
    // Cache for free path-tracing and compute a stable (union) type list so the
    // filter categories don't flicker frame-to-frame.
    this.loadedSteps = steps;
    // Only a non-empty load is worth reusing; '' keeps a failed or empty window
    // from being served back as a cache hit.
    this._stepsCacheKey = steps.length ? cacheKey : '';
    const union: any[] = [];
    for (const step of steps) union.push(...(step.features ?? []));
    this.ctx.display.computeAvailableTypes(union);
    return steps;
  }

  // ── Frame rendering ─────────────────────────────────────────────────────────
  /**
   * Renders a single playback frame. Keeps exactly one layer alive (clearing the
   * previous frame's markers) so scrubbing and playing stay cheap. A null step
   * clears the current frame (used on close / resume-live).
   */
  renderStep(step: PlaybackStep | null): void {
    if (!step) {
      if (this.playbackLayer) {
        this.ctx.host.map.removeLayer(this.playbackLayer);
        this.playbackLayer = null;
      }
      // The bubbles belonged to the frame that just came off. Leaving them would
      // park the last frame's counts over the live layer that replaces it —
      // `restoreLiveLayer` runs the display pass again and rebuilds its own.
      this.ctx.display.clearVesselClusters();
      // Same for the selection ring, which was riding a marker in that frame.
      // `restoreLiveLayer`'s pass puts it back on the live hull; when there is no
      // live layer to go back to, it correctly stays off.
      this.ctx.selection.syncActiveHalo(null);
      // And any saved-vessel marker that had collapsed to a ring around a hull in
      // that frame: the hull left with the frame, so the diamond comes back
      // rather than leaving an empty ring on the water.
      this.ctx.located.syncLocatedTwins();
      return;
    }
    const sensor = this.ctx.sensor;
    // One animation at a time: entering snapshot playback drops any vessel track,
    // the mirror of `buildTrackLayers` leaving playback when a path is loaded.
    //
    // Gated on the first frame of the replay rather than asked on every one. It
    // has to stay ABOVE the live-layer hide below: closing a track runs a display
    // pass over the live layer to undim the fleet, and that pass must not be
    // handed a layer that has already been detached from the map.
    if (!this.playbackLayer && this.ctx.host.track.hasTrack) {
      this.ctx.host.track.close();
    }
    // Hide the live vessel layer while animating so the map never shows the
    // static live snapshot and the animated frame at the same time.
    if (this.liveLayer && this.ctx.host.map.hasLayer(this.liveLayer)) {
      this.ctx.host.map.removeLayer(this.liveLayer);
      this._liveLayerHidden = true;
    }
    // Reuse a single GeoJSON layer across frames (clear + addData) rather than
    // tearing down and recreating every marker each tick.
    if (!this.playbackLayer) {
      this.playbackLayer = (L as any)
        .geoJSON(null, {
          pointToLayer: (feature: any, latlng: any) =>
            this.ctx.host.displayDirectionInMap(feature, latlng, sensor),
          onEachFeature: (feature: any, layer: any) => {
            // Vessels stay selectable mid-playback, so track mode can be entered
            // from any frame. The popup is built on click rather than bound per
            // feature: onEachFeature runs for every vessel of every frame, and
            // bindPopup would allocate a Popup per vessel ~10x a second.
            layer.on('click', () => {
              this.ctx.selection.activateVessel(feature.properties, layer);
              // Through the popup port — the ONE place the standard card is
              // opened at a point, so the four layers that raise it cannot
              // drift apart in options or content.
              this.ctx.popup.openVesselPopupAt(layer.getLatLng(), feature.properties);
            });
          },
        })
        .addTo(this.ctx.host.map);
    }
    this.playbackLayer.clearLayers();
    this.currentFeatures = step.features ?? [];
    const filtered = this.ctx.display.applyTypeFilter(this.currentFeatures);
    this.playbackLayer.addData(
      this.ctx.host.convertToGeoJSONNew(filtered, this.opts.geoJsonType),
    );
    // After the data, not inside `onEachFeature`: clusters and labels are both
    // decided against the OTHER vessels in the frame, so the pass has to see the
    // whole frame first. A replay stays clustered for the same reason the live
    // view is — a flip-book of a thousand overlapping arrows is no more readable
    // for moving.
    this.ctx.display.applyVesselDisplay(this.playbackLayer);
  }

  /** Puts back the live layer that entering playback hid. No-op otherwise. */
  restoreLiveLayer(): void {
    if (!this._liveLayerHidden || !this.liveLayer) {
      // Playback can be started from a cold map, in which case there was no
      // live layer to hide and none to put back. Its frames seeded the
      // categories from the union of every loaded snapshot, and those would
      // otherwise outlive the last frame — leaving the Vessel Types section on
      // screen, filtering nothing, over an empty map.
      if (!this.dataActive) this.ctx.host.playback.resetTypeFilter();
      return;
    }
    this.ctx.host.map.addLayer(this.liveLayer);
    this._liveLayerHidden = false;
    // Entering playback replaced the categories with the union across every
    // loaded snapshot, which is a superset of what the live layer holds. Put
    // the live set back so the Vessel Types section does not keep offering
    // chips for types that left the map with the last frame.
    this.currentFeatures = this._liveFeatures;
    this.ctx.display.computeAvailableTypes(this._liveFeatures);
    this.ctx.display.applyTypeFilterToLive();
  }

  /**
   * Called by the host once live vessels are on the map: adopts the layer, seeds
   * the ship-type categories and re-applies the active filter. The layer is
   * rebuilt from scratch on every pull, so without the re-apply a refresh would
   * silently show every type again while the toolbar still reports a filter.
   */
  adoptLiveLayer(layer: any, features: any[]): void {
    this.dataActive = true;
    this.liveLayer = layer;
    this.currentFeatures = Array.isArray(features) ? features : [];
    this._liveFeatures = this.currentFeatures;
    this.indexLiveVessels(this.currentFeatures);
    // After the index, whose de-duplicated size is what the dim acts on — the
    // raw feature list can carry one MMSI more than once. A selection dropped
    // further down by `reanchorActiveVessel` re-pushes this through
    // `onActiveChange`, so the count never describes a vessel that has gone.
    this.ctx.host.track.setOthersOnMap(
      this.othersOnMap(this.ctx.selection.activeMmsi())
    );
    this.ctx.display.computeAvailableTypes(this.currentFeatures);
    this.ctx.display.applyTypeFilterToLive();
    // Last, and after the filter pass: the selection is reconciled against the
    // markers that are actually visible, and the dim it may re-apply must not be
    // undone by that pass.
    this.ctx.selection.reanchorActiveVessel();
    // History vessels stay LIST-ONLY (drawn on demand from the panel), so a
    // snapshot arriving only recomputes the rows and arms the popup action.
    this.ctx.popup.ensurePopupActionsWired();
    this.ctx.located.pushLocatedState();
    // A snapshot landing is the moment the free search goes from having nothing
    // to filter to having a fleet.
    this.opts.pushSearchContext();
  }

  /**
   * Rebuilds the MMSI → name/colour index the Vessel Tracks panel reads.
   *
   * Both the mapped Feature keys and the raw feed keys are accepted, because
   * `adoptLiveLayer` is called from two host paths that differ in which shape
   * they hand over.
   */
  indexLiveVessels(features: any[]): void {
    this.liveVessels.clear();
    for (const f of features || []) {
      const p = f?.properties || f || {};
      const mmsi = String(p['MMSI'] ?? p.mmsi ?? '');
      if (!mmsi) continue;
      this.liveVessels.set(mmsi, {
        name: p['Name'] ?? p.name ?? null,
        color: p['Color'] ?? p.color ?? null,
      });
    }
  }

  /**
   * Vessels on the map that a track's dim would fade — the snapshot minus the
   * traced hull, which is held at its own fade for as long as the track is up
   * (`TrackLayers._tracedMmsi`).
   *
   * On `SnapshotPort` since phase 7: the track feature had an identical copy
   * reading the same `liveVessels`, and both push the answer to the same
   * control. One derivation, owned by the module that owns the hulls.
   *
   * The traced vessel is not always IN the snapshot: a track replayed from the
   * Vessel Tracks panel can belong to a hull that has left the AOI, or be drawn
   * over an empty map entirely, which is the case this count exists for.
   */
  othersOnMap(tracedMmsi: string | null): number {
    const total = this.liveVessels.size;
    return tracedMmsi && this.liveVessels.has(tracedMmsi) ? total - 1 : total;
  }

  /**
   * Drops the live-layer bookkeeping without touching the map itself.
   *
   * `keepLayerRef` exists because the two callers have always differed:
   * `removeDataSourceById` clears the reference (that layer is gone for good),
   * while `removeVesselLayer` clears only the flags. The asymmetry looks
   * incidental, but resolving it is not this extraction's job — preserved as-is.
   */
  releaseLiveLayer(keepLayerRef = false): void {
    this.dataActive = false;
    this.currentFeatures = [];
    this._liveFeatures = [];
    // The bubbles counted the vessels that just left, so they go with them —
    // otherwise a cleared map keeps a scatter of counts over empty water. Also
    // stops the viewport listener: there is nothing left for a pan to recompute,
    // and the next `adoptLiveLayer` re-attaches it.
    this.ctx.display.clearVesselClusters();
    this.ctx.display.stopViewportRepaint();
    // The selection ring goes with the hull it was drawn on. Nothing else takes
    // it off here: the display pipeline (which normally decides this) only runs
    // over a layer, and there is no layer left to run over — so a halo would sit
    // pulsing over empty water until something happened to draw vessels again.
    //
    // The SELECTION survives, like the type filter above: this also runs mid-swap
    // between snapshots, where `adoptLiveLayer` re-anchors the ring onto the same
    // vessel in the layer that lands next. A map emptied for real goes through
    // `dropSelectionWithMap`.
    this.ctx.selection.syncActiveHalo(null);
    // The presence dots in the Vessel Tracks panel describe the layer that just
    // went, so they go with it — every vessel reads "not in view" until the next
    // pull, which is the truth.
    this.liveVessels.clear();
    // Same reason: with the fleet gone there is nothing left for the track bar's
    // dim button to fade, so it takes itself off the bar until the next pull.
    this.ctx.host.track.setOthersOnMap(0);
    if (!keepLayerRef) this.liveLayer = null;
    // The ship-type categories describe the layer that just went, so they go
    // with it — that is what hides the Vessel Types section instead of leaving
    // it listing types for a map with nothing on it. The user's SELECTION is
    // left alone: this also runs mid-swap between snapshots, where dropping it
    // would clear the filter on every step through history. Clearing the map
    // for real goes through `resetTypeFilter()`.
    // `?? []` only because the structural host contract types this field as
    // optional (D7 amendment) — every real state object sets it.
    if ((this.ctx.host.playback.snapshot().availableTypes ?? []).length) {
      this.ctx.host.playback.setAvailableTypes([]);
    }
    // The located overlay stays — a searched vessel does not belong to the
    // snapshot that left.
    this.ctx.located.pushLocatedState();
    // The map is empty again, so the drawer goes back to naming the act that
    // would fill it rather than offering a filter over nothing.
    this.opts.pushSearchContext();
  }

  // ── Toolbar refresh ─────────────────────────────────────────────────────────
  /**
   * Toolbar Refresh → pull a fresh AOI snapshot. Delegates to the host's
   * `fetchAISDataFromServer` so the entitlement guards, popups, spinner,
   * refresh-history push and rendering stay in one place (identical to
   * re-checking the sensor). Resolves once the pull settles, so the toolbar's
   * spinner tracks the real request.
   */
  refresh(): Promise<void> {
    // No sensor context from the map toolbar, so it acts on the first of the
    // provider's sensors — unchanged behaviour: `ctx.sensor` is by definition
    // that same first-sensor lookup. The panel's button knows which row it
    // belongs to and passes it, which is the correct one on a two-sensor task.
    return this.refreshSensor(this.ctx.sensor);
  }

  /**
   * Pull a fresh AOI snapshot for a specific sensor.
   *
   * Deliberately calls `fetchAISDataFromServer` directly rather than going
   * through `toggleSensorNew` the way the old sensor checkbox did: that path is
   * several hundred lines of shared, operator-agnostic teardown (map reset,
   * prediction clearing, scene re-selection) whose only purpose on this branch
   * was to arrive here. Entitlement guards, popups, spinner and the
   * refresh-history push all live in `fetchAISDataFromServer` either way.
   */
  refreshSensor(sensor: any): Promise<void> {
    // Playback owns the map while animating — refreshing under it would fight
    // the frame renderer, so ignore the click until the user exits playback.
    if (this.ctx.host.playback.mode === 'playback') return Promise.resolve();
    const sensors = this.ctx.host.collection?.sensors ?? [];
    const sensorIndex = sensors.indexOf(sensor);
    if (!sensor || sensorIndex === -1) return Promise.resolve();
    const scene = sensor?.scenes?.find((s: any) => s?.checked) ?? sensor?.scenes?.[0];
    if (!scene) return Promise.resolve();

    // `checked` is no longer a control, but it is still STATE: it is what marks
    // this sensor as having data on the map, and several readers downstream
    // (the timeseries overlay, `clearVoyageSnapshot`, the scene lookup above)
    // depend on it. The old checkbox set it as a side effect of being ticked;
    // now the refresh sets it deliberately. Failure paths inside
    // `fetchAISDataFromServer` still reset it, and the ON MAP bar's clear still
    // unsets it, so the flag's lifecycle is unchanged.
    sensor.checked = true;

    return new Promise<void>((resolve) => {
      this._refreshResolve = resolve;
      this._refreshStarted = false;
      try {
        this.ctx.host.fetchAISDataFromServer(this.ctx.host.collection, sensorIndex, scene);
      } catch {
        this.settleRefresh();
        return;
      }
      // Every guard path (TOI not started, expired, refreshes exhausted) returns
      // synchronously without firing a request — settle now so the button resets.
      if (!this._refreshStarted) this.settleRefresh();
    });
  }

  /** The host calls this once a refresh request is genuinely in flight. */
  markRefreshStarted(): void {
    this._refreshStarted = true;
  }

  /** Completes the promise handed to the toolbar's refresh control (once). */
  settleRefresh(): void {
    const done = this._refreshResolve;
    this._refreshResolve = null;
    this._refreshStarted = false;
    done?.();
  }

  // ── Vessel icons ────────────────────────────────────────────────────────────
  /**
   * The vessel glyph as a data URL, cached.
   *
   * Encoding a 24px PNG through `canvas.toDataURL()` is expensive, and playback
   * asks for one per vessel per frame — roughly 10x/second across the whole AOI.
   * Without this, a busy snapshot re-encodes thousands of identical images a
   * second, which is what used to hang the flip-book.
   *
   * The key is (view, colour) only. Ship type is NOT part of it: unlike Spire —
   * which maps type to colour via `getHistoricalShipColor` — Voyage Radar takes
   * the colour straight from the feed and falls back to one constant per view,
   * so two ship types with the same colour are the same image.
   */
  vesselIconUrl(isHistory: boolean, color?: string): string {
    const key = `${isHistory ? 'history' : 'live'}|${color ?? ''}`;
    const cached = this._iconCache.get(key);
    if (cached) return cached;

    const iconColor = color || (isHistory ? 'YELLOW' : 'ORANGE');
    const canvas = isHistory
      ? this.ctx.host.createHistoricalNavigationIcon(iconColor)
      : this.ctx.host.createNavigationIcon(iconColor);
    const url = canvas.toDataURL('image/png');
    this._iconCache.set(key, url);
    return url;
  }

  /**
   * Nulls the playback bookkeeping WITHOUT touching the map — for the
   * task-switch reset, which runs inside a shared sweep that has already taken
   * every layer off (invariant I1: a removeLayer here would operate on a map
   * that no longer holds it). The step cache goes too: a stale key would serve
   * the previous task's frames to the next one. The live-layer bookkeeping has
   * its own teardown (`releaseLiveLayer`), which the facade's reset calls next.
   */
  dropRefs(): void {
    this.playbackLayer = null;
    this._liveLayerHidden = false;
    this.loadedSteps = [];
    this._stepsCacheKey = '';
  }
}
